import { EffectivePlanPricing, PricingCadence } from './promotionCode';

// Pure copy/math helpers for the redesigned (B-side) plan tiles of the
// pricing A/B test (ORA-392). Kept free of React so they unit-test directly.

/**
 * Sublabel under the big price. Names the term the displayed price covers and
 * — for multi-month plans — the rough equivalent monthly rate ("for your
 * first year — about $33/mo"). Returns null when there is nothing to say
 * (monthly at its ongoing price).
 */
export const getRedesignSublabel = (
  pricing: EffectivePlanPricing,
  cadence: PricingCadence,
  accessYears: number
): string | null => {
  if (cadence === 'one_time') {
    const equivalentMonthly = pricing.discountedPrice / (accessYears * 12);
    return `one payment — about $${Math.round(equivalentMonthly)}/mo over ${accessYears} years`;
  }

  if (cadence === 'annual') {
    const about = `about $${Math.round(pricing.discountedPrice / 12)}/mo`;

    const isTemporaryDiscount =
      pricing.isDiscountActive && pricing.durationType !== 'forever';

    return isTemporaryDiscount ? `for your first year — ${about}` : about;
  }

  // Monthly: only a temporary discount gives the price a term worth naming;
  // the ongoing rate (no discount, or a `forever` coupon) needs no sublabel.
  if (!pricing.isDiscountActive || pricing.durationType === 'forever') {
    return null;
  }
  if (pricing.durationType === 'once') return 'for your first month';

  const months = pricing.durationMonths;
  if (!months) return 'during the promotion';
  return months === 1
    ? 'for your first month'
    : `for your first ${months} months`;
};

/**
 * Whole-percent saving of a plan's equivalent monthly rate vs. the monthly
 * plan's current (discounted) rate, for the "X% less than monthly" pill.
 * Null when the comparison is meaningless or not a saving.
 */
export const getSavingsVsMonthlyPercent = (
  planPricing: EffectivePlanPricing,
  monthlyPricing: EffectivePlanPricing,
  monthsCovered: number
): number | null => {
  const monthlyRate = monthlyPricing.discountedPrice;
  if (monthlyRate <= 0 || monthsCovered <= 0) return null;

  const equivalentMonthly = planPricing.discountedPrice / monthsCovered;
  const percent = Math.round((1 - equivalentMonthly / monthlyRate) * 100);

  return percent > 0 ? percent : null;
};

// Stripe coupon names carry a per-plan suffix ("Summer Sale — MONTHLY",
// "School Launch Offer — 50% MONTHLY", "Special Offer — STUDENT_LIFE") that
// is internal bookkeeping, redundant inside that plan's own tile.
const PLAN_KEY_SUFFIX =
  /\s*[—–-]\s*(\d{1,3}%\s*)?(monthly|annual|five[_\s-]?year|student[_\s-]?life)$/i;

/**
 * Strips a trailing per-plan suffix from an automatic campaign/coupon name.
 * Automatic discounts only — a raw promo code (e.g. "SAVE-MONTHLY") must be
 * shown verbatim, so callers keep custom-promotion names away from this.
 */
export const cleanDiscountName = (name: string | null): string | null => {
  if (!name) return null;
  const cleaned = name.replace(PLAN_KEY_SUFFIX, '').trim();
  return cleaned || null;
};

/**
 * Discount pill text: the badge ("33% OFF") plus the campaign / promo-code
 * name when one exists ("33% OFF — Summer Sale"; the pill uppercases via
 * CSS). Null when the plan has no active discount.
 */
export const getDiscountPillLabel = (
  pricing: EffectivePlanPricing,
  discountName: string | null
): string | null => {
  if (!pricing.isDiscountActive || !pricing.badgeLabel) return null;
  return discountName
    ? `${pricing.badgeLabel} — ${discountName}`
    : pricing.badgeLabel;
};
